import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { _roleEnumUserClass } from 'app/utils/role.utils';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { AppConstants } from 'app/app.constants';
import {
  AuthorizationService,
  RBAC,
} from 'app/core/services/authorization.service';
import { Subject, Subscription } from 'rxjs';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogFreeTierComponent } from 'app/shared/components/dialog-free-tier/dialog-free-tier.component';
import { UserResources } from 'app/shared/models/role';

const EXTERNAL_LINK_TARGET = '_blank';
const EXTERNAL_LINK_FEATURES = 'noopener,noreferrer';

interface DashboardCard {
  title: string;
  iconClass?: string;
  imgSrc?: string;
  description: string;
  callback: () => void;
  disabled: boolean;
  comingSoon?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  @Input() receivedLinkButton: string;
  public userId: string;
  public metadata;
  public string;
  public modalSignUp = true;
  public nomeUsuarioLogado: any;
  public isFreeTier: boolean = true;
  public auth: RBAC;
  public showOffCanvasSubject: Subject<boolean> = new Subject();
  public isCardDisabled;

  private readonly authState = inject(AuthStateService);

  showOffCanvas: boolean = false;
  userRole = '';
  ROLE_USERS_ENUM = _roleEnumUserClass.ROLE_ENUM;
  cards: DashboardCard[] = [];
  cardDisabled: boolean = false;
  languageChangeSubscription: Subscription;
  userID: string;
  currentLang: string = 'ptBR';

  /**
   * Whether the campaign carousel found anything to show.
   *
   * @remarks
   * Starts `null` — meaning "the carousel has not answered yet" — so neither block
   * is rendered while the request is in flight. Defaulting to `false` would flash
   * the mobile app promo on every visit and then swap it for the carousel.
   */
  hasCampaigns: boolean | null = null;

  /**
   * Whether the campaign carousel should be shown.
   *
   * @remarks
   * Campaign content comes straight from the backend with no per-language
   * localization — it is always written in Portuguese — so the carousel only
   * makes sense for Portuguese-speaking users. Every other language slot goes
   * straight to the mobile app promo instead.
   */
  get isPortugueseLanguage(): boolean {
    return this.currentLang === AppConstants.LANGUAGES.PT_BR;
  }

  appStoreLink: string = AppConstants.MOBILE_APP.APP_STORE_URL;
  googlePlayLink: string = AppConstants.MOBILE_APP.GOOGLE_PLAY_URL;

  constructor(
    public translateService: TranslateService,
    public toastr: ToastrService,
    public router: Router,
    public route: ActivatedRoute,
    public authorizationService: AuthorizationService,
    private readonly analyticsService: AnalyticsService,
    private readonly dialog: MatDialog,
  ) {
    this.auth = authorizationService.gethandler();
    this.route.queryParams.subscribe((params) => {
      const msg = params['msg'];
      if (msg != null && msg != '') {
        this.toastr.warning(msg);
      }
    });
  }

  async ngOnInit() {
    this.userRole = this.authorizationService.getLoggedUserRole();
    this.currentLang = this.translateService.currentLang || 'ptBR';
    this.languageChangeSubscription =
      this.translateService.onLangChange.subscribe((event) => {
        this.currentLang = event.lang;
        this.hasCampaigns = null;
        this.mountCards();
      });
    const usuarioLogado = this.authState.getUser();
    this.userId = usuarioLogado?.id;

    this.nomeUsuarioLogado = (usuarioLogado as any)?.Nome;
    this.isFreeTier = _roleEnumUserClass.isRoleFreetier(usuarioLogado);

    this.analyticsService.sendPageView('/dashboard/');
    this.analyticsService.trackEvent(
      'Accessed the dashboard page',
      'Dashboard',
    );

    const hideModal: boolean =
      window.localStorage.getItem(
        AppConstants.KEYS_LOCAL_STORAGE.HIDE_MODAL,
      ) === 'true';
    if (this.isFreeTier && !hideModal) {
      const dialogRef = this.dialog.open(DialogFreeTierComponent, {
        width: '600px',
        height: '240px',
        disableClose: false,
        enterAnimationDuration: '200ms',
        exitAnimationDuration: '200ms',
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.router.navigate(['/manage/my-profile']);
      });
    }

    this.mountCards();
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

  /**
   * Records what the campaign carousel resolved to.
   *
   * @param hasCampaigns - `true` when at least one campaign was served.
   *
   * @remarks
   * The two blocks are mutually exclusive: an active campaign is a message
   * somebody deliberately published, so it takes the slot; with none to show, the
   * space goes back to promoting the mobile app instead of collapsing.
   */
  onCarouselResolved(hasCampaigns: boolean): void {
    this.hasCampaigns = hasCampaigns;
  }

  async mountCards() {
    const translationKeys = [
      'dashboard.title-navigation-report',
      'dashboard.title-navigation-msg',
      'dashboard.title-monitoring-fleet',
      'dashboard.title-monitoring-msg',
      'dashboard.title-field',
      'dashboard.title-field-msg',
      'dashboard.title-weather',
      'dashboard.title-weather-msg',
      'dashboard.title-support',
      'dashboard.title-support-msg',
      'dashboard.title-data-transfer',
      'dashboard.description-data-transfer-msg',
      'dashboard.title-agrodrive',
      'dashboard.title-agrodrive-msg',
      'dashboard.title-academy',
      'dashboard.title-academy-msg',
    ];

    this.translateService.get(translationKeys).subscribe((translations) => {
      const titleNavigationReport =
        translations['dashboard.title-navigation-report'];
      const descriptionNavigationReport =
        translations['dashboard.title-navigation-msg'];
      const titleMonitoringFleet =
        translations['dashboard.title-monitoring-fleet'];
      const descriptionMonitoringFleet =
        translations['dashboard.title-monitoring-msg'];
      const titleFieldNotebook = translations['dashboard.title-field'];
      const descriptionFieldNotebook =
        translations['dashboard.title-field-msg'];
      const titleWeatherForecast = translations['dashboard.title-weather'];
      const descriptionWeatherForecast =
        translations['dashboard.title-weather-msg'];
      const titleHelpCenter = translations['dashboard.title-support'];
      const descriptionHelpCenter = translations['dashboard.title-support-msg'];
      const titleDataTransfer = translations['dashboard.title-data-transfer'];
      const descriptionDataTransfer =
        translations['dashboard.description-data-transfer-msg'];
      const titleAgrodrive = translations['dashboard.title-agrodrive'];
      const descriptionAgrodrive =
        translations['dashboard.title-agrodrive-msg'];
      const titleAcademy = translations['dashboard.title-academy'];
      const descriptionAcademy = translations['dashboard.title-academy-msg'];

      this.cards = [
        {
          title: titleNavigationReport,
          iconClass: 'agres-navigation-report',
          description: descriptionNavigationReport,
          callback: () => this.myReportCardClickedHandler(),
          disabled: this.myReportCardClickedDisable(),
        },
        {
          title: titleMonitoringFleet,
          iconClass: 'agres-fleet-monitoring',
          description: descriptionMonitoringFleet,
          callback: () => this.myFleetMonitoringCardClickedHandler(),
          disabled: this.myFleetMonitoringCardClickedDisable(),
        },
        {
          title: titleFieldNotebook,
          iconClass: 'agres-field',
          description: descriptionFieldNotebook,
          callback: () => this.myFieldNotebookCardClickedHandler(),
          disabled: this.myFieldNotebookCardClickedDisable(),
        },
        {
          title: titleWeatherForecast,
          iconClass: 'agres-weather-sun',
          description: descriptionWeatherForecast,
          callback: () => this.myWeatherForecastCardClickedHandler(),
          disabled: this.myWeatherForecastCardClickedDisable(),
        },
        {
          title: titleHelpCenter,
          iconClass: 'agres-help',
          description: descriptionHelpCenter,
          callback: () => this.myHelpCenterCardClickedHandler(),
          disabled: this.myHelpCenterCardClickedDisable(),
        },
        {
          title: titleDataTransfer,
          iconClass: 'agres-transfer',
          description: descriptionDataTransfer,
          callback: () => this.myAgrirouterCardClickedHandler(),
          disabled: this.myHelpCenterCardClickedDisable(),
        },
        {
          title: titleAgrodrive,
          iconClass: 'agres-upload-cloud',
          description: descriptionAgrodrive,
          callback: () => this.myAgrodriveCardClickedHandler(),
          disabled: this.myAgrodriveCardClickedDisable(),
          comingSoon: true,
        },
        {
          title: titleAcademy,
          iconClass: 'agres-play-circle',
          description: descriptionAcademy,
          callback: () => this.myAcademyCardClickedHandler(),
          disabled: false,
        },
      ];
    });
  }

  navigateAndHandleClick(callback: () => void) {
    callback();
  }

  navigate(paginaSelecionada: string) {
    this.router.navigate([paginaSelecionada]);
  }

  handleCardClick(resource: UserResources, path: string) {
    if (this.auth.permits(this.auth.permissions.NAVIGATE, resource)) {
      this.navigate(path);
    } else {
      this.translateService
        .get('global.alert.users-premium-msg')
        .subscribe((msg: string) => {
          this.toastr.warning(msg);
        });
    }
  }

  dashboard() {
    this.router.navigate(['/']);
  }

  myReportCardClickedHandler() {
    this.handleCardClick(
      this.auth.resources.NAVIGATION_RESULT,
      '../gestao-operacao/gestao-arquivo',
    );
  }

  myFleetMonitoringCardClickedHandler() {
    this.handleCardClick(
      this.auth.resources.FIELD_ADMINISTRATION,
      '../gestao-operacao/monitoramento-frota',
    );
  }

  myWeatherForecastCardClickedHandler() {
    this.handleCardClick(
      this.auth.resources.WEATHER_FORECAST,
      '../gestao-operacao/previsao-tempo',
    );
  }

  myFieldNotebookCardClickedHandler() {
    this.handleCardClick(
      this.auth.resources.FIELD_ADMINISTRATION,
      '../my-fields',
    );
  }

  myHelpCenterCardClickedHandler() {
    this.handleCardClick(
      this.auth.resources.FIELD_ADMINISTRATION,
      '../suporte-tecnico',
    );
  }
  myAgrirouterCardClickedHandler() {
    this.navigate('../manage/connections');
  }

  myAgrodriveCardClickedHandler() {
    this.navigate('../agrodrive');
  }

  /**
   * Opens the Agres Academy portal in a new tab.
   *
   * @remarks
   * The portal lives outside the app, so it is a plain external link with no
   * role check — the training content is public to every logged user.
   */
  myAcademyCardClickedHandler() {
    window.open(
      AppConstants.EXTERNAL_LINKS.AGRES_ACADEMY,
      EXTERNAL_LINK_TARGET,
      EXTERNAL_LINK_FEATURES,
    );
  }

  myReportCardClickedDisable() {
    return this.auth.forbids(
      this.auth.permissions.NAVIGATE,
      this.auth.resources.NAVIGATION_RESULT,
    );
  }

  myFleetMonitoringCardClickedDisable(): boolean {
    return (
      this.auth &&
      this.auth.forbids(
        this.auth.permissions.NAVIGATE,
        this.auth.resources.FIELD_ADMINISTRATION,
      )
    );
  }

  myWeatherForecastCardClickedDisable() {
    return this.auth.forbids(
      this.auth.permissions.NAVIGATE,
      this.auth.resources.WEATHER_FORECAST,
    );
  }

  myFieldNotebookCardClickedDisable(): boolean {
    return (
      this.auth &&
      this.auth.forbids(
        this.auth.permissions.NAVIGATE,
        this.auth.resources.FIELD_ADMINISTRATION,
      )
    );
  }

  myHelpCenterCardClickedDisable() {
    return this.auth.forbids(
      this.auth.permissions.NAVIGATE,
      this.auth.resources.FIELD_ADMINISTRATION,
    );
  }
  myAgrodriveCardClickedDisable() {
    return this.auth.forbids(
      this.auth.permissions.NAVIGATE,
      this.auth.resources.FIELD_ADMINISTRATION,
    );
  }
}
